import React from 'react';
import { withStyles } from '@material-ui/core/styles';
import { Flex, Box } from '@rebass/grid';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import { CardContent, Divider } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import PageLayout from '../../page-layout';
import { useQueryClient, useMutation } from 'react-query';
import { usePendingFeedback } from '../../../lib/hooks/use-user-feedback';
import {
  approveFeedback,
  rejectFeedback,
} from '../../../lib/mutations/feedback';
import { useParams } from '@reach/router';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { colors } from '../../../config/theme';

const styles = {
  page: {
    padding: '16px',
  },
};

const MOCK_PENDING = [
  {
    id: 1,
    comment:
      'Alex showed great improvement in guard passing today. Keep encouraging him to chain techniques.',
    member_id: 101,
    member_name: 'Alex Johnson',
    author_id: 201,
    author_name: 'Coach Maria',
    gym_id: 2,
    status: 0,
    approved_by_id: null,
    approved_on: null,
    created_at: '2025-08-01T18:30:00Z',
    updated_at: '2025-08-01T18:30:00Z',
  },
  {
    id: 2,
    comment:
      'Brianna struggled with takedown entries; recommend extra reps on single-leg setups.',
    member_id: 102,
    member_name: 'Brianna Lee',
    author_id: 202,
    author_name: 'Coach Dan',
    gym_id: 2,
    status: 0,
    approved_by_id: null,
    approved_on: null,
    created_at: '2025-08-02T00:12:00Z',
    updated_at: '2025-08-02T00:12:00Z',
  },
  {
    id: 3,
    comment:
      'Excellent discipline and helped newer students. Consider highlighting him in next class.',
    member_id: 103,
    member_name: 'Carlos Mendoza',
    author_id: 203,
    author_name: 'Coach Aisha',
    gym_id: 2,
    status: 0,
    approved_by_id: null,
    approved_on: null,
    created_at: '2025-08-03T15:05:00Z',
    updated_at: '2025-08-03T15:05:00Z',
  },
];

const FeedbackQueue = (props) => {
  const { classes } = props;
  const { gymUid } = useParams();
  const queryClient = useQueryClient();
  const { pending = [], isLoading } = usePendingFeedback(gymUid);

  const isMock =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('mock') === '1';
  const items = isMock ? MOCK_PENDING : pending;
  const isTabletOrLess = useMediaQuery('(max-width:900px)');

  const { mutate: approve, isLoading: approving } = useMutation(
    (feedbackId) => approveFeedback(feedbackId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['pending-feedback', gymUid]);
      },
    }
  );
  const { mutate: reject, isLoading: rejecting } = useMutation(
    (feedbackId) => rejectFeedback(feedbackId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['pending-feedback', gymUid]);
      },
    }
  );

  return (
    <PageLayout selected="Feedback Queue" pageTitle="Feedback Queue">
      <Box className={classes.page}>
        <Typography variant="h5">Coach Feedback Queue</Typography>
        <Typography variant="body2" color="textSecondary">
          Approve or reject feedback submitted by coaches before it appears on
          user profiles.
        </Typography>
        <Card style={{ marginTop: '16px' }}>
          <CardContent>
            {isLoading && !isMock ? (
              <Typography>Loading...</Typography>
            ) : items.length === 0 ? (
              <Box py={2}>
                <Typography color="textSecondary" align="center">
                  No pending feedback
                </Typography>
              </Box>
            ) : (
              <List>
                {items.map((item) => (
                  <React.Fragment key={item.id}>
                    <ListItem
                      alignItems="flex-start"
                      style={{
                        display: 'flex',
                        flexDirection: isTabletOrLess ? 'column' : 'row',
                        alignItems: isTabletOrLess ? 'stretch' : 'flex-start',
                      }}
                    >
                      <ListItemText
                        style={{ flex: 1 }}
                        primary={item.comment || ''}
                        secondary={
                          (item.member_name
                            ? `Member: ${item.member_name} — `
                            : '') + (item.author_name || 'Unknown')
                        }
                      />
                      {isTabletOrLess ? (
                        <Box mt="8px">
                          <Flex>
                            <Box mr="8px" width={1 / 2}>
                              <Button
                                fullWidth
                                variant="outlined"
                                color="primary"
                                onClick={() => approve(item.id)}
                                disabled={approving || isMock}
                              >
                                Approve
                              </Button>
                            </Box>
                            <Box width={1 / 2}>
                              <Button
                                fullWidth
                                variant="outlined"
                                style={{
                                  borderColor: colors.danger,
                                  color: colors.danger,
                                }}
                                onClick={() => reject(item.id)}
                                disabled={rejecting || isMock}
                              >
                                Reject
                              </Button>
                            </Box>
                          </Flex>
                        </Box>
                      ) : (
                        <Flex alignItems="center" ml="8px">
                          <Box mr="8px">
                            <Button
                              variant="outlined"
                              color="primary"
                              onClick={() => approve(item.id)}
                              disabled={approving || isMock}
                            >
                              Approve
                            </Button>
                          </Box>
                          <Button
                            variant="outlined"
                            style={{ borderColor: colors.danger, color: colors.danger }}
                            onClick={() => reject(item.id)}
                            disabled={rejecting || isMock}
                          >
                            Reject
                          </Button>
                        </Flex>
                      )}
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Box>
    </PageLayout>
  );
};

export default withStyles(styles)(FeedbackQueue);
